import React, { useState } from "react";
import { ChevronDown } from "lucide-react";

import {
  BROKER_ALERT_CLASS,
  BROKER_ALERT_DESC_CLASS,
  BROKER_ALERT_TITLE_CLASS,
  BrokerAlertIcon,
  brokerAlertVariant,
} from "@/components/Broker/brokerAlertUi";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { FieldLabel } from "@/components/ui/field";
import { cn } from "@/lib/utils";
import type { CsvImportWarning } from "../../../types/customCsvImport";
import {
  csvImportWarningAlertStatus,
  formatCsvImportWarningDisplayLine,
  highestWarningSeverity,
  summarizeCsvImportWarningsForDisplay,
} from "../../../utils/customCsvImportWarnings";

type AckCheckboxProps = {
  codes: string[];
  acknowledged: Set<string>;
  onToggle: (codes: string[], checked: boolean) => void;
};

const AckCheckbox = ({
  codes,
  acknowledged,
  onToggle,
}: AckCheckboxProps): React.ReactElement => {
  const allChecked = codes.every((code) => acknowledged.has(code));
  const inputId = `custom-csv-warning-ack-${codes.join("-")}`;

  return (
    <div className="mt-3 border-t border-border pt-3">
      <div className="flex min-w-0 items-start gap-3">
        <Checkbox
          id={inputId}
          data-custom-csv-ack-checkbox=""
          checked={allChecked}
          className="mt-0.5"
          onCheckedChange={(checked) => onToggle(codes, checked === true)}
        />
        <FieldLabel
          htmlFor={inputId}
          className={cn(BROKER_ALERT_DESC_CLASS, "cursor-pointer font-normal")}
        >
          I understand
        </FieldLabel>
      </div>
    </div>
  );
};

export type GroupedWarningAlertProps = {
  warnings: CsvImportWarning[];
  acknowledged: Set<string>;
  onToggle: (codes: string[], checked: boolean) => void;
  illustration?: React.ReactNode;
};

const GroupedWarningAlert = ({
  warnings,
  acknowledged,
  onToggle,
  illustration,
}: GroupedWarningAlertProps): React.ReactElement => {
  const [detailsOpen, setDetailsOpen] = useState(true);
  const codes = warnings.map((w) => w.code);
  const severity = highestWarningSeverity(warnings);
  const alertVariant = brokerAlertVariant(csvImportWarningAlertStatus(severity));
  const alertTone =
    alertVariant === "error" ? "error" : alertVariant === "warning" ? "warning" : "info";
  const displayLines = summarizeCsvImportWarningsForDisplay(warnings);
  const messages = displayLines.map((line) => formatCsvImportWarningDisplayLine(line));
  const summaryLabel =
    warnings.length === 1 ? "1 notice" : `${warnings.length} notices`;
  const collapsedSummary =
    warnings.length === 1
      ? messages[0]!.length > 96
        ? `${messages[0]!.slice(0, 96).trim()}…`
        : messages[0]!
      : summaryLabel;

  return (
    <div className="mb-4">
      {illustration ? <div className="mb-3">{illustration}</div> : null}
      <Alert
        variant={alertVariant}
        className={cn(BROKER_ALERT_CLASS, "has-[>svg]:gap-x-3 sm:has-[>svg]:gap-x-4")}
      >
        <BrokerAlertIcon tone={alertTone} />
        <AlertDescription className={cn(BROKER_ALERT_DESC_CLASS, "col-start-2 min-w-0")}>
          <Collapsible open={detailsOpen} onOpenChange={setDetailsOpen} className="min-w-0">
            <div className="flex items-start justify-between gap-3">
              <p className={cn(BROKER_ALERT_TITLE_CLASS, "m-0 min-w-0 flex-1")}>
                {detailsOpen ? summaryLabel : collapsedSummary}
              </p>
              <CollapsibleTrigger
                type="button"
                className={cn(
                  "inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5",
                  "font-[family-name:var(--font-primary)] text-xs font-normal",
                  "text-[color:var(--brand-tropical-surf)]",
                  "hover:bg-[color-mix(in_oklab,var(--brand-tropical-surf)_12%,transparent)]",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--brand-neon-aqua)]"
                )}
                aria-label={detailsOpen ? "Hide warning details" : "Show warning details"}
              >
                {detailsOpen ? "Hide" : "Show"}
                <ChevronDown
                  aria-hidden
                  className={cn(
                    "size-3.5 transition-transform duration-200",
                    detailsOpen && "rotate-180"
                  )}
                />
              </CollapsibleTrigger>
            </div>

            <CollapsibleContent className="pt-2">
              <div className="flex flex-col gap-2.5">
                {messages.map((message, index) => (
                  <p key={displayLines[index]?.codes.join("-") ?? index} className="m-0 leading-5">
                    {message}
                  </p>
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>

          <AckCheckbox codes={codes} acknowledged={acknowledged} onToggle={onToggle} />
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default GroupedWarningAlert;
